import AddressAutocomplete from '@orn/shared-web/components/AddressAutocomplete';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { ResidenceType } from '../../../__generated__/globalTypes';
import { useUpsertResidence } from '../../../hooks/mutations/useUpsertResidence';
import { useCurrentUser } from '../../../hooks/queries/useCurrentUser';
import { useOnboardingState } from '../../../hooks/queries/useOnboardingState';
import { getOptionalEnv } from '../../../lib/getEnv';
import { OnboardingTitle } from '../../OnboardingLayout';

import OnboardingStepActions from './OnboardingStepActions';

import type { IOnboardingStepProps } from '../types';
import type { IParsedAddress } from '@orn/shared-web/components/AddressAutocomplete';

/**
 * "Where do you live?" — the first data-bearing step in the About You phase
 * after phone verification, sitting between {@link PhoneNumberVerifyStep} and
 * {@link GreatStartStep}. Captures the caregiver's primary residence via
 * Google Places typeahead so we get structured fields (street, city, state,
 * lat/lng) rather than free text — proximity to the loved one drives
 * downstream travel / care suggestions.
 *
 * The typeahead is restricted to street-address Place types so cities and
 * regions never appear as suggestions, and Continue gates on
 * `streetNumber` + `streetName` + `city` + `country` as a defensive
 * double-check. On submit, persists to the family-scoped `residences` table
 * via {@link useUpsertResidence} as the user's `isPrimary: true` row, then
 * advances. See the inline note on `residenceType` for why we default to
 * `House` here.
 *
 * Falls back to an empty `apiKey` when `VITE_GOOGLE_PLACES_API_KEY` is unset
 * so Storybook and key-less local dev still render — the typeahead surfaces
 * its "Could not load suggestions" empty state in that case.
 *
 * Figma: Mantle - UX/UI, node 5263:79667 ("Onboarding Step 31").
 */
const UserResidenceStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const apiKey = getOptionalEnv('VITE_GOOGLE_PLACES_API_KEY') ?? '';
  const { user } = useCurrentUser();
  const { primaryResidence, refetch: refetchOnboardingState } = useOnboardingState();
  const [upsertResidence] = useUpsertResidence();
  // Lazy initial state — the orchestrator gates mounting on
  // `useOnboardingState`'s loading flag, so `primaryResidence` is already
  // resolved by the time we render. Re-pick by the user replaces this seed;
  // re-renders don't overwrite it. `streetNumber` / `streetName` are absent
  // (the DB stores them combined as `addressLine1`), so the `canSubmit` gate
  // below treats a seeded pick as already-validated rather than re-asserting
  // those fields.
  const [picked, setPicked] = React.useState<IParsedAddress | null>(() =>
    primaryResidence
      ? {
          formattedAddress: primaryResidence.formatted ?? '',
          placeId: primaryResidence.placeId ?? '',
          subpremise: primaryResidence.addressLine2 ?? undefined,
          city: primaryResidence.city,
          state: primaryResidence.state ?? undefined,
          postalCode: primaryResidence.postalCode ?? undefined,
          country: primaryResidence.country,
        }
      : null
  );
  // Tracks a residence id created in this session so a re-submit before the
  // onboarding-query cache refreshes still routes through the update path
  // (`residences_primary_per_user_idx` allows at most one primary per user).
  const [createdResidenceId, setCreatedResidenceId] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const existingResidenceId = createdResidenceId ?? primaryResidence?.id ?? null;

  const handleInputChange = React.useCallback((next: string) => {
    // Editing after a pick invalidates the structured selection — force the
    // user to choose another suggestion before continuing.
    setPicked((prev) => (prev && prev.formattedAddress === next ? prev : null));
  }, []);

  const canSubmit = Boolean(
    user?.id &&
    picked &&
    picked.city &&
    picked.country &&
    !isSubmitting &&
    // Either a fresh Google pick (with structured street fields verified) or
    // a seeded value from the persisted row (already validated at create
    // time). `includedPrimaryTypes={['street_address']}` on the typeahead is
    // the real enforcement; this gate is a defensive double-check.
    (existingResidenceId !== null || (picked.streetNumber && picked.streetName))
  );

  const handleSubmit = React.useCallback(async () => {
    if (!canSubmit || !picked || !user) {
      return;
    }
    // Fresh Google pick: combine the structured street fields. Seeded pick
    // (no street fields parsed out — DB stores them combined): fall back to
    // the persisted `addressLine1` so a no-op resubmit preserves the value.
    const addressLine1 =
      picked.streetNumber && picked.streetName
        ? `${picked.streetNumber} ${picked.streetName}`
        : (primaryResidence?.addressLine1 ?? null);

    setIsSubmitting(true);
    try {
      const result = await upsertResidence({
        variables: {
          input: {
            // Update path: pass the existing row id and let the resolver ignore
            // the (create-only) owner pointer. Create path: omit `residenceId`
            // and the resolver picks up `userId` from the input.
            residenceId: existingResidenceId,
            userId: existingResidenceId ? null : user.id,
            isPrimary: true,
            // No type picker on this screen; default to House so the DB CHECK
            // (`residenceTypeOther` text required iff type=OTHER) is satisfied.
            residenceType: ResidenceType.House,
            addressLine1,
            addressLine2: picked.subpremise ?? null,
            // `city` and `country` are non-null on the wire; the `canSubmit`
            // gate above guarantees they're present here.
            city: picked.city ?? '',
            state: picked.state ?? null,
            postalCode: picked.postalCode ?? null,
            country: picked.country ?? '',
            placeId: picked.placeId || null,
            formatted: picked.formattedAddress || null,
          },
        },
      });
      const upsertedId = result.data?.upsertResidence.id ?? null;
      if (upsertedId && !existingResidenceId) {
        setCreatedResidenceId(upsertedId);
        // Create path only: the server-side `User.primaryResidence` field
        // resolver doesn't know about the new row from a normalized-cache
        // merge alone (the reference on User is still `null`). On the update
        // path the merge handles it automatically — no refetch needed.
        await refetchOnboardingState();
      }
      onNext();
    } catch (error) {
      logger.error('Failed to upsert primary residence', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, existingResidenceId, onNext, picked, primaryResidence, refetchOnboardingState, user, upsertResidence]);

  return (
    <>
      <OnboardingTitle
        title="Where do you live?"
        subtitle="Knowing your location helps us calculate your proximity to your loved one."
        onBack={onBack}
      />

      <div className="flex w-full flex-col gap-4">
        <AddressAutocomplete
          apiKey={apiKey}
          label="Address"
          placeholder="123 Main St., Apt. 5, San Francisco, CA"
          defaultValue={primaryResidence?.formatted ?? ''}
          // Restrict suggestions to street-level results so the user can't
          // pick a city or region as their primary residence.
          includedPrimaryTypes={['street_address']}
          onSelect={setPicked}
          onInputChange={handleInputChange}
        />
      </div>

      <OnboardingStepActions
        isContinueDisabled={!canSubmit}
        isContinueLoading={isSubmitting}
        onContinue={() => void handleSubmit()}
      />
    </>
  );
};

UserResidenceStep.displayName = 'UserResidenceStep';

export default UserResidenceStep;
